import Checkbox from '@atoms/Checkbox/Checkbox';
import Select from '@atoms/Select/Select';
import Text from '@atoms/Text/Text';
import VariantTypeEnum from '@models/enums/VariantTypeEnum';
import { IFilter, IFilterGroup } from '@models/IFilterGroup';
import IFormComponent from '@models/IFormComponent';
import { ChangeEvent } from 'react';

interface IFilterSection extends IFormComponent {
  readonly countries: (IFilter & { readonly languages?: IFilter[] })[];
  readonly countryPlaceholder: string;
  readonly dataTestId: string;
  readonly disableLanguageSelection: boolean;
  readonly filters: IFilterGroup;
  readonly onCountryChange: (countryItem: IFilter, reset: boolean) => void;
  readonly languagePlaceholder: string;
  readonly languages: IFilter[];
  readonly selectedLanguage: IFilter | null;
  readonly setSelectedLanguage: (newSelectedItem: IFilter | null) => void;
  readonly selectedCountry: IFilter | null;
  readonly setSelectedCountry: (newSelectedItem: IFilter | null) => void;
  readonly selectedFileTypes: string[];
  readonly setSelectedFileTypes: (newSelectedItem: string[]) => void;
}

const FilterSection = ({
  countries,
  countryPlaceholder,
  dataTestId,
  disableLanguageSelection,
  filters,
  onCountryChange,
  languagePlaceholder,
  languages,
  register,
  selectedLanguage,
  setSelectedLanguage,
  selectedCountry,
  setSelectedCountry,
  selectedFileTypes,
  setSelectedFileTypes,
  setValue,
}: IFilterSection) => {
  const onCheckboxChange = (
    e: ChangeEvent<HTMLInputElement>,
    filterId: string
  ) => {
    const isChecked = e.target?.checked;
    const prevFileTypes = [...selectedFileTypes];
    const parsedFilterId = String(filters.id);
    if (isChecked) {
      setSelectedFileTypes([...prevFileTypes, filterId]);
      setValue?.(parsedFilterId, [...selectedFileTypes, filterId]);
    } else {
      setSelectedFileTypes(prevFileTypes?.filter((id) => id !== filterId));
      setValue?.(
        parsedFilterId,
        selectedFileTypes.filter((id) => id !== filterId)
      );
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-xs xl:tw-gap-sm">
      <Select
        dataTestId={`${dataTestId}-Country`}
        displayLabel={false}
        name="location"
        onSelectedItemChange={(item) => {
          setValue?.('language', '');
          if (item) {
            setSelectedCountry(item);
            onCountryChange(item, true);
          }
        }}
        options={countries}
        placeholder={countryPlaceholder}
        register={register}
        selectedItem={selectedCountry}
        setValue={setValue}
        variant={VariantTypeEnum.Primary}
      />
      <Select
        dataTestId={`${dataTestId}-Language`}
        disabled={disableLanguageSelection}
        displayLabel={false}
        name="language"
        onSelectedItemChange={setSelectedLanguage}
        options={languages}
        placeholder={languagePlaceholder}
        register={register}
        selectedItem={selectedLanguage}
        setValue={setValue}
        variant={VariantTypeEnum.Primary}
      />
      <div>
        <Text
          className="tw-inline-block tw-pb-xs tw-text-grey-70"
          variant="label-12"
        >
          {filters.label}
        </Text>
        <div className="tw-flex tw-flex-col tw-items-start tw-justify-between tw-gap-sm xl:tw-flex-row">
          {filters.options.map((filter, index) => {
            const filterId = String(filter.id);
            return (
              <Checkbox
                checked={selectedFileTypes.includes(filterId)}
                dataTestId={`${dataTestId}-Filter-Item-${index}`}
                onChange={(e) => onCheckboxChange(e, filterId)}
                key={filter.id}
                label={filter.label}
                name={filterId}
                register={register}
                value={filter.id}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FilterSection;
